import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as returnsService from '@/services/returnsService';
import type {
  ReturnFilter,
  CreateReturnRequest,
  UpdateReturnRequest,
  ReturnStatus,
} from '@/types/returns';

// ============================================================================
// Query Keys
// ============================================================================

export const RETURNS_QUERY_KEYS = {
  all: ['returns'] as const,
  lists: () => [...RETURNS_QUERY_KEYS.all, 'list'] as const,
  list: (filter?: ReturnFilter) => [...RETURNS_QUERY_KEYS.lists(), filter] as const,
  details: () => [...RETURNS_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...RETURNS_QUERY_KEYS.details(), id] as const,
};

// ============================================================================
// Returns Hooks
// ============================================================================

export function useReturns(filter?: ReturnFilter) {
  return useQuery(
    RETURNS_QUERY_KEYS.list(filter),
    () => returnsService.listReturns(filter),
    {
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
}

export function useReturn(id: string | undefined) {
  return useQuery(
    RETURNS_QUERY_KEYS.detail(id!),
    () => returnsService.getReturn(id!),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );
}

export function useCreateReturn() {
  const queryClient = useQueryClient();

  return useMutation(
    (request: CreateReturnRequest) => returnsService.createReturn(request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(RETURNS_QUERY_KEYS.lists());
      },
    }
  );
}

export function useUpdateReturn() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, request }: { id: string; request: UpdateReturnRequest }) =>
      returnsService.updateReturn(id, request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(RETURNS_QUERY_KEYS.lists());
        queryClient.setQueryData(RETURNS_QUERY_KEYS.detail(variables.id), data);
      },
    }
  );
}

export function useUpdateReturnStatus() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, status }: { id: string; status: ReturnStatus }) =>
      returnsService.updateReturnStatus(id, status),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(RETURNS_QUERY_KEYS.lists());
        queryClient.invalidateQueries(RETURNS_QUERY_KEYS.detail(variables.id));
      },
    }
  );
}

export function useCompleteReturn() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => returnsService.completeReturn(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(RETURNS_QUERY_KEYS.lists());
      },
    }
  );
}

export function useDeleteReturn() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => returnsService.deleteReturn(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(RETURNS_QUERY_KEYS.lists());
      },
    }
  );
}
